import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CalendarCheck, ChevronDown, ChevronRight, Zap, CheckCircle2, Clock, Circle, X, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const CAT_COLORS = {
  'Fiscales': { bar: 'bg-red-500', badge: 'bg-red-100 text-red-800' },
  'Contabilidad': { bar: 'bg-blue-500', badge: 'bg-blue-100 text-blue-800' },
  'Electrónicos': { bar: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800' },
  'Informes a Gerencia': { bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800' },
};

const MonthlyClose = () => {
  const { user } = useAuth();
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [cierres, setCierres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [detailCierre, setDetailCierre] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [filterResponsable, setFilterResponsable] = useState('Todos');
  const [users, setUsers] = useState([]);

  const mes = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cierresRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cierre-mensual?mes=${mes}`),
        axios.get(`${BACKEND_URL}/api/users`).catch(() => ({ data: [] })),
      ]);
      setCierres(cierresRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Error loading data');
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGenerate = async () => {
    if (!window.confirm(`¿Generar cierre mensual de ${MONTHS[selectedMonth - 1]} ${selectedYear} para todos los clientes?`)) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/cierre-mensual/generar`, { mes });
      alert(data.message);
      loadData();
    } catch (err) {
      alert('Error al generar el cierre');
    } finally {
      setGenerating(false);
    }
  };

  const syncResponsables = async () => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/cierre-mensual/sync-responsables`, {});
      alert(data.message);
      loadData();
    } catch {
      alert('Error al sincronizar');
    }
  };

  const openDetail = (cierre) => {
    setDetailCierre(cierre);
    setShowDetail(true);
  };

  const deleteCierre = async (e, cierreId, clientName) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar el seguimiento de "${clientName}" para este mes?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/cierre-mensual/${cierreId}`);
      setCierres(prev => prev.filter(c => c._id !== cierreId));
    } catch {
      alert('Error al eliminar');
    }
  };

  const updateActividad = async (cierreId, catIdx, actIdx, nuevoEstado) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/cierre-mensual/${cierreId}/actividad`, {
        categoria_idx: catIdx,
        actividad_idx: actIdx,
        estado: nuevoEstado,
      });
      // Update local state
      setDetailCierre(prev => {
        const cats = prev.categorias.map((c, ci) => {
          if (ci !== catIdx) return c;
          const acts = c.actividades.map((a, ai) => ai === actIdx ? { ...a, estado: nuevoEstado } : a);
          return { ...c, actividades: acts };
        });
        return { ...prev, categorias: cats };
      });
      // Update list
      setCierres(prev => prev.map(c => {
        if (c._id !== cierreId) return c;
        const cats = c.categorias.map((cat, ci) => {
          if (ci !== catIdx) return cat;
          const acts = cat.actividades.map((a, ai) => ai === actIdx ? { ...a, estado: nuevoEstado } : a);
          return { ...cat, actividades: acts };
        });
        // Recalculate progress
        let total = 0, finalizado = 0, en_proceso = 0, pendiente = 0;
        cats.forEach(cat => cat.actividades.forEach(a => {
          total++;
          if (a.estado === 'Finalizado') finalizado++;
          else if (a.estado === 'En Proceso') en_proceso++;
          else pendiente++;
        }));
        return {
          ...c, categorias: cats,
          progreso: {
            total, finalizado, en_proceso, pendiente,
            pct_finalizado: total ? Math.round(finalizado / total * 100) : 0,
            pct_en_proceso: total ? Math.round(en_proceso / total * 100) : 0,
            pct_pendiente: total ? Math.round(pendiente / total * 100) : 0,
          }
        };
      }));
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  const getEstadoIcon = (estado) => {
    if (estado === 'Finalizado') return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    if (estado === 'En Proceso') return <Clock className="w-4 h-4 text-blue-500" />;
    return <Circle className="w-4 h-4 text-slate-300" />;
  };

  const nextEstado = (estado) => {
    if (estado === 'Pendiente') return 'En Proceso';
    if (estado === 'En Proceso') return 'Finalizado';
    return 'Pendiente';
  };

  // Filtered cierres
  const filteredCierres = filterResponsable === 'Todos'
    ? cierres
    : cierres.filter(c => c.responsable_id === filterResponsable);

  // Global progress (filtered)
  const globalProgress = filteredCierres.reduce((acc, c) => {
    acc.total += c.progreso?.total || 0;
    acc.finalizado += c.progreso?.finalizado || 0;
    acc.en_proceso += c.progreso?.en_proceso || 0;
    return acc;
  }, { total: 0, finalizado: 0, en_proceso: 0 });
  const globalPct = globalProgress.total ? Math.round(globalProgress.finalizado / globalProgress.total * 100) : 0;

  return (
    <div className="space-y-6" data-testid="monthly-close-page">
      <div>
        <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Seguimiento Mensual
        </h1>
        <p className="text-sm text-slate-700 leading-relaxed mt-2">Control del cierre mensual por cliente</p>
      </div>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Mes</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(+e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="month-select">
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Año</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(+e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="year-select">
              {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {user?.role === 'Administrador' && (
            <div className="flex gap-2">
              <button onClick={handleGenerate} disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-[#1F3864] hover:bg-[#172b4d] disabled:bg-slate-300 text-white rounded-md text-sm font-medium transition-colors"
                data-testid="generate-month-btn">
                <Zap className="w-4 h-4" />
                {generating ? 'Generando...' : 'Generar Mes'}
              </button>
              {cierres.length > 0 && (
                <button onClick={syncResponsables}
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium transition-colors"
                  data-testid="sync-responsables-btn" title="Sincronizar responsables de clientes con el seguimiento">
                  Sincronizar
                </button>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Responsable</label>
            <select value={filterResponsable} onChange={(e) => setFilterResponsable(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="filter-responsable">
              <option value="Todos">Todos</option>
              {users.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-6">
            {filteredCierres.length > 0 && (
              <div className="text-right">
                <p className="text-xs text-slate-500">Avance global</p>
                <p className="text-2xl font-bold text-[#1F3864]">{globalPct}%</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Global progress bar */}
      {filteredCierres.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-4 mb-2">
            <span className="text-xs font-semibold text-slate-600">{MONTHS[selectedMonth - 1]} {selectedYear}</span>
            <span className="text-xs text-slate-500">{filteredCierres.length} clientes | {globalProgress.finalizado}/{globalProgress.total} actividades</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
            {globalProgress.total > 0 && (
              <>
                <div className="bg-emerald-500 transition-all" style={{ width: `${globalProgress.finalizado / globalProgress.total * 100}%` }} />
                <div className="bg-blue-400 transition-all" style={{ width: `${globalProgress.en_proceso / globalProgress.total * 100}%` }} />
              </>
            )}
          </div>
          <div className="flex gap-4 mt-1.5 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Finalizado</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-400" />En Proceso</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-slate-200" />Pendiente</span>
          </div>
        </div>
      )}

      {/* Summary by Responsable */}
      {cierres.length > 0 && filterResponsable === 'Todos' && user?.role === 'Administrador' && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
          <h3 className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">Avance por Responsable</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(() => {
              const byResp = {};
              cierres.forEach(c => {
                const name = c.responsable_name || 'Sin asignar';
                if (!byResp[name]) byResp[name] = { total: 0, finalizado: 0, clientes: 0 };
                byResp[name].clientes += 1;
                byResp[name].total += c.progreso?.total || 0;
                byResp[name].finalizado += c.progreso?.finalizado || 0;
              });
              return Object.entries(byResp).map(([name, data]) => {
                const pct = data.total ? Math.round(data.finalizado / data.total * 100) : 0;
                return (
                  <div key={name} className="border border-slate-100 rounded-lg p-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => {
                      const uid = users.find(u => u.name === name)?._id;
                      setFilterResponsable(uid || 'Todos');
                    }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-slate-800">{name}</span>
                      <span className="text-lg font-bold text-[#1F3864]">{pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-1">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-500">{data.clientes} clientes | {data.finalizado}/{data.total} actividades</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Client Cards */}
      {loading ? (
        <p className="text-sm text-slate-500 text-center py-8">Cargando...</p>
      ) : filteredCierres.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
          <CalendarCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No hay cierre generado para {MONTHS[selectedMonth - 1]} {selectedYear}</p>
          <p className="text-sm text-slate-500 mt-1">Usa el botón "Generar Mes" para crear el cierre de este periodo</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cierre-cards">
          {filteredCierres.map((cierre) => {
            const p = cierre.progreso || {};
            return (
              <div key={cierre._id}
                onClick={() => openDetail(cierre)}
                className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all group"
                data-testid={`cierre-card-${cierre.client_id}`}>
                
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 leading-tight">{cierre.client_name}</h3>
                    <p className="text-[10px] text-slate-500 mt-0.5">{cierre.responsable_name || ''}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {user?.role === 'Administrador' && (
                      <button onClick={(e) => deleteCierre(e, cierre._id, cierre.client_name)}
                        className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                        title="Eliminar del seguimiento" data-testid={`cierre-delete-${cierre.client_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )
                    }
                    <span className={`text-xl font-bold flex-shrink-0 ${
                    p.pct_finalizado === 100 ? 'text-emerald-600' :
                    p.pct_finalizado >= 50 ? 'text-blue-600' : 'text-slate-600'
                  }`}>
                    {p.pct_finalizado}%
                  </span>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden flex mb-3">
                  <div className="bg-emerald-500 transition-all" style={{ width: `${p.pct_finalizado}%` }} />
                  <div className="bg-blue-400 transition-all" style={{ width: `${p.pct_en_proceso}%` }} />
                </div>

                {/* Category mini progress */}
                <div className="space-y-1.5">
                  {cierre.categorias.map((cat) => {
                    const catTotal = cat.actividades.length;
                    const catDone = cat.actividades.filter(a => a.estado === 'Finalizado').length;
                    const style = CAT_COLORS[cat.categoria] || { bar: 'bg-slate-400', badge: 'bg-slate-100 text-slate-800' };
                    return (
                      <div key={cat.categoria} className="flex items-center gap-2">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${style.badge} whitespace-nowrap`}>
                          {cat.categoria.length > 12 ? cat.categoria.substring(0, 12) + '.' : cat.categoria}
                        </span>
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${style.bar} transition-all`}
                            style={{ width: `${catTotal ? (catDone / catTotal * 100) : 0}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-500 font-medium w-8 text-right">{catDone}/{catTotal}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailCierre?.client_name} — {MONTHS[selectedMonth - 1]} {selectedYear}</DialogTitle>
          </DialogHeader>
          {detailCierre && (
            <div className="space-y-4 mt-2">
              {/* Responsable selector */}
              {user?.role === 'Administrador' && (
                <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                  <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">Responsable:</span>
                  <select
                    value={detailCierre.responsable_id || ''}
                    onChange={async (e) => {
                      const newResp = e.target.value;
                      try {
                        await axios.patch(`${BACKEND_URL}/api/cierre-mensual/${detailCierre._id}/responsable`, { responsable_id: newResp });
                        const respName = users.find(u => u._id === newResp)?.name || 'Sin asignar';
                        setDetailCierre(prev => ({ ...prev, responsable_id: newResp, responsable_name: respName }));
                        setCierres(prev => prev.map(c => c._id === detailCierre._id ? { ...c, responsable_id: newResp, responsable_name: respName } : c));
                      } catch { alert('Error al cambiar responsable'); }
                    }}
                    className="border border-slate-300 rounded-md px-2 py-1 text-sm"
                    data-testid="detail-responsable-select"
                  >
                    {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                  </select>
                </div>
              )}
              {user?.role !== 'Administrador' && detailCierre.responsable_name && (
                <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                  <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">Responsable:</span>
                  <span className="text-sm text-slate-800 font-medium">{detailCierre.responsable_name}</span>
                </div>
              )}
              {detailCierre.categorias.map((cat, catIdx) => {
                const style = CAT_COLORS[cat.categoria] || { badge: 'bg-slate-100 text-slate-800' };
                const done = cat.actividades.filter(a => a.estado === 'Finalizado').length;
                return (
                  <div key={cat.categoria}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${style.badge}`}>{cat.categoria}</span>
                      <span className="text-xs text-slate-500">{done}/{cat.actividades.length}</span>
                    </div>
                    <div className="space-y-1">
                      {cat.actividades.map((act, actIdx) => (
                        <button key={actIdx}
                          onClick={() => updateActividad(detailCierre._id, catIdx, actIdx, nextEstado(act.estado))}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-left transition-all ${
                            act.estado === 'Finalizado' ? 'bg-emerald-50 hover:bg-emerald-100' :
                            act.estado === 'En Proceso' ? 'bg-blue-50 hover:bg-blue-100' :
                            'bg-slate-50 hover:bg-slate-100'
                          }`}
                          data-testid={`detail-act-${catIdx}-${actIdx}`}>
                          {getEstadoIcon(act.estado)}
                          <span className={act.estado === 'Finalizado' ? 'line-through text-slate-500' : 'text-slate-800'}>
                            {act.nombre}
                          </span>
                          <span className={`ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            act.estado === 'Finalizado' ? 'bg-emerald-100 text-emerald-700' :
                            act.estado === 'En Proceso' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-200 text-slate-600'
                          }`}>
                            {act.estado === 'Finalizado' ? '100%' : act.estado === 'En Proceso' ? '50%' : '0%'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-slate-400 text-center pt-2">Haz clic en una actividad para cambiar su estado: Pendiente → En Proceso → Finalizado</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyClose;
